// ============================================
// God Rays (Crepuscular Rays) Post-Process Effect
// ============================================
// Screen-space light shafts radiating from a configurable light position:
//   1. Bright mask     — soft-threshold + radial falloff around the light (half res)
//   2. Radial blur ×N  — dithered march toward the light; each extra iteration
//                        shrinks the step by samples^-k, so 2 iterations of 32
//                        taps behave like 32² (three.js GodRaysPass technique)
//   3. Composite       — tinted additive/screen blend over the scene
//
// The light position is in viewport UV (0-1). Animate it from gameplay code by
// writing lightX/lightY params on the camera's post-process stack.

import type { IRenderContext } from '../../core/IRenderContext';
import { OffscreenTarget } from '../../core/RenderPass';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import {
  GOD_RAYS_MASK_LAYOUT,
  GOD_RAYS_BLUR_LAYOUT,
  GOD_RAYS_COMPOSITE_LAYOUT,
} from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * God Rays post-processing effect (volumetric light shafts)
 */
export class GodRaysEffect extends PostProcessEffect {
  readonly type = 'godRays';
  readonly displayName = 'God Rays';

  // === Light ===
  lightX = 0.5;
  lightY = 0.3;

  // === Mask ===
  threshold = 0.8;
  softKnee = 0.5;
  radiusStart = 0.1;   // radial falloff start (UV distance from the light)
  radiusEnd = 0.9;     // radial falloff end

  // === Ray march ===
  density = 0.9;       // how far along the pixel→light line the march reaches
  decay = 0.95;        // per-tap attenuation
  weight = 1.0;        // per-tap weight
  samples = 32;        // taps per iteration
  iterations = 2;      // blur iterations (1-3): each one massively smooths the rays
  dither = 1.0;        // jitter to hide banding

  // === Composite ===
  exposure = 1.0;
  blendMode = 0;       // 0=additive, 1=screen
  tintR = 1.0;
  tintG = 0.95;
  tintB = 0.8;

  // GPU resources
  private maskShader: ShaderHandle = NULL_HANDLE;
  private blurShader: ShaderHandle = NULL_HANDLE;
  private compositeShader: ShaderHandle = NULL_HANDLE;

  private maskPipeline: PipelineHandle = NULL_HANDLE;
  private blurPipeline: PipelineHandle = NULL_HANDLE;
  private compositePipelineHDR: PipelineHandle = NULL_HANDLE;
  private compositePipelineLDR: PipelineHandle = NULL_HANDLE;

  // Half-res ping-pong for mask + blur iterations
  private rayTargetA: OffscreenTarget | null = null;
  private rayTargetB: OffscreenTarget | null = null;

  private maskWriter = GOD_RAYS_MASK_LAYOUT.createWriter();
  private blurWriter = GOD_RAYS_BLUR_LAYOUT.createWriter();
  private compositeWriter = GOD_RAYS_COMPOSITE_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      // Light
      { name: 'lightX', type: 'float', min: -0.5, max: 1.5, default: 0.5, description: 'Light X (viewport UV)' },
      { name: 'lightY', type: 'float', min: -0.5, max: 1.5, default: 0.3, description: 'Light Y (viewport UV)' },

      // Mask
      { name: 'threshold', type: 'float', min: 0, max: 3, default: 0.8, description: 'Brightness threshold' },
      { name: 'softKnee', type: 'float', min: 0, max: 1, default: 0.5, description: 'Soft knee' },
      { name: 'radiusStart', type: 'float', min: 0, max: 2, default: 0.1, description: 'Falloff start radius' },
      { name: 'radiusEnd', type: 'float', min: 0.05, max: 2, default: 0.9, description: 'Falloff end radius' },

      // March
      { name: 'density', type: 'float', min: 0.1, max: 1.5, default: 0.9, description: 'Ray length (fraction of pixel→light)' },
      { name: 'decay', type: 'float', min: 0.8, max: 1, default: 0.95, description: 'Per-sample decay' },
      { name: 'weight', type: 'float', min: 0, max: 3, default: 1.0, description: 'Per-sample weight' },
      { name: 'samples', type: 'int', min: 8, max: 64, default: 32, description: 'Samples per iteration' },
      { name: 'iterations', type: 'int', min: 1, max: 3, default: 2, description: 'Blur iterations' },
      { name: 'dither', type: 'float', min: 0, max: 1, default: 1.0, description: 'Banding dither' },

      // Composite
      { name: 'exposure', type: 'float', min: 0, max: 5, default: 1.0, description: 'Ray exposure' },
      { name: 'blendMode', type: 'int', min: 0, max: 1, default: 0, description: 'Blend (0=add, 1=screen)' },
      { name: 'tintR', type: 'float', min: 0, max: 2, default: 1.0, description: 'Tint red' },
      { name: 'tintG', type: 'float', min: 0, max: 2, default: 0.95, description: 'Tint green' },
      { name: 'tintB', type: 'float', min: 0, max: 2, default: 0.8, description: 'Tint blue' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'lightX': return this.lightX;
      case 'lightY': return this.lightY;
      case 'threshold': return this.threshold;
      case 'softKnee': return this.softKnee;
      case 'radiusStart': return this.radiusStart;
      case 'radiusEnd': return this.radiusEnd;
      case 'density': return this.density;
      case 'decay': return this.decay;
      case 'weight': return this.weight;
      case 'samples': return this.samples;
      case 'iterations': return this.iterations;
      case 'dither': return this.dither;
      case 'exposure': return this.exposure;
      case 'blendMode': return this.blendMode;
      case 'tintR': return this.tintR;
      case 'tintG': return this.tintG;
      case 'tintB': return this.tintB;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'lightX': this.lightX = value; break;
      case 'lightY': this.lightY = value; break;
      case 'threshold': this.threshold = Math.max(0, value); break;
      case 'softKnee': this.softKnee = Math.max(0, Math.min(1, value)); break;
      case 'radiusStart': this.radiusStart = Math.max(0, value); break;
      case 'radiusEnd': this.radiusEnd = Math.max(0.05, value); break;
      case 'density': this.density = Math.max(0.05, Math.min(2, value)); break;
      case 'decay': this.decay = Math.max(0.5, Math.min(1, value)); break;
      case 'weight': this.weight = Math.max(0, value); break;
      case 'samples': this.samples = Math.floor(Math.max(4, Math.min(64, value))); break;
      case 'iterations': this.iterations = Math.floor(Math.max(1, Math.min(3, value))); break;
      case 'dither': this.dither = Math.max(0, Math.min(1, value)); break;
      case 'exposure': this.exposure = Math.max(0, value); break;
      case 'blendMode': this.blendMode = Math.floor(Math.max(0, Math.min(1, value))); break;
      case 'tintR': this.tintR = Math.max(0, value); break;
      case 'tintG': this.tintG = Math.max(0, value); break;
      case 'tintB': this.tintB = Math.max(0, value); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const maskWgsl = getCompiledShader('postprocess/god-rays-mask');
    const blurWgsl = getCompiledShader('postprocess/god-rays-blur');
    const compositeWgsl = getCompiledShader('postprocess/god-rays-composite');
    this.maskShader = ctx.createShader(maskWgsl, maskWgsl);
    this.blurShader = ctx.createShader(blurWgsl, blurWgsl);
    this.compositeShader = ctx.createShader(compositeWgsl, compositeWgsl);

    this.maskPipeline = ctx.createPipeline({
      shader: this.maskShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'god-rays-mask',
    });
    this.blurPipeline = ctx.createPipeline({
      shader: this.blurShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'god-rays-blur',
    });
    this.compositePipelineHDR = ctx.createPipeline({
      shader: this.compositeShader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 2 },
      label: 'god-rays-composite-hdr',
    });
    this.compositePipelineLDR = ctx.createPipeline({
      shader: this.compositeShader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 2 },
      label: 'god-rays-composite-ldr',
    });
  }

  protected onResize(ctx: IRenderContext, width: number, height: number): void {
    this.rayTargetA?.destroy();
    this.rayTargetB?.destroy();

    const halfW = Math.max(1, Math.floor(width / 2));
    const halfH = Math.max(1, Math.floor(height / 2));
    this.rayTargetA = new OffscreenTarget(ctx, halfW, halfH, false, true);
    this.rayTargetB = new OffscreenTarget(ctx, halfW, halfH, false, true);
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, destination, width, height, intensity, isHdrTarget } = context;

    if (!this.rayTargetA || !this.rayTargetB) {
      this.onResize(ctx, width, height);
    }
    if (!this.rayTargetA || !this.rayTargetB) return;

    const halfW = this.rayTargetA.width;
    const halfH = this.rayTargetA.height;

    // All passes land in one queue submit — fullscreenPass() snapshots each pass's uniforms.

    // === Pass 1: bright mask (half res) ===
    ctx.setRenderTarget(this.rayTargetA.handle);
    ctx.clear(0, 0, 0, 1);
    ctx.setViewport(0, 0, halfW, halfH);

    this.maskWriter.setAll({
      lightPos: [this.lightX, this.lightY],
      threshold: this.threshold,
      softKnee: this.softKnee,
      radiusStart: this.radiusStart,
      radiusEnd: this.radiusEnd,
      aspect: width / Math.max(height, 1),
      _pad: 0,
    });
    this.fullscreenPass(ctx, this.maskPipeline, this.maskWriter.float32, [source]);

    // === Pass 2..N: iterative radial blur (ping-pong A→B→A…) ===
    let readTarget = this.rayTargetA;
    let writeTarget = this.rayTargetB;

    for (let pass = 0; pass < this.iterations; pass++) {
      ctx.setRenderTarget(writeTarget.handle);
      ctx.clear(0, 0, 0, 1);
      ctx.setViewport(0, 0, halfW, halfH);

      this.blurWriter.setAll({
        lightPos: [this.lightX, this.lightY],
        // Every extra iteration divides the step by the tap count — the march of pass k fills
        // the gaps between pass k-1's taps (effective taps = samples^iterations).
        stepScale: this.density * Math.pow(this.samples, -pass),
        // Decay shapes the falloff once; the refinement passes just interpolate.
        decay: pass === 0 ? this.decay : 1.0,
        weight: pass === 0 ? this.weight : 1.0,
        samples: this.samples,
        dither: this.dither,
        _pad: 0,
      });
      this.fullscreenPass(ctx, this.blurPipeline, this.blurWriter.float32, [readTarget.getTexture()]);

      const tmp = readTarget;
      readTarget = writeTarget;
      writeTarget = tmp;
    }

    // === Final pass: composite over the scene ===
    ctx.setRenderTarget(destination);
    ctx.clear(0, 0, 0, 1);
    ctx.setViewport(0, 0, width, height);

    this.compositeWriter.setAll({
      intensity: intensity, // context.intensity already includes this.intensity
      exposure: this.exposure,
      blendMode: this.blendMode,
      _pad0: 0,
      tint: [this.tintR, this.tintG, this.tintB],
      _pad1: 0,
    });
    this.fullscreenPass(
      ctx,
      isHdrTarget ? this.compositePipelineHDR : this.compositePipelineLDR,
      this.compositeWriter.float32,
      [source, readTarget.getTexture()],
    );
  }

  protected onDispose(ctx: IRenderContext): void {
    this.rayTargetA?.destroy();
    this.rayTargetB?.destroy();
    this.rayTargetA = null;
    this.rayTargetB = null;

    if (this.maskPipeline !== NULL_HANDLE) ctx.destroyPipeline(this.maskPipeline);
    if (this.blurPipeline !== NULL_HANDLE) ctx.destroyPipeline(this.blurPipeline);
    if (this.compositePipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.compositePipelineHDR);
    if (this.compositePipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.compositePipelineLDR);
    if (this.maskShader !== NULL_HANDLE) ctx.destroyShader(this.maskShader);
    if (this.blurShader !== NULL_HANDLE) ctx.destroyShader(this.blurShader);
    if (this.compositeShader !== NULL_HANDLE) ctx.destroyShader(this.compositeShader);

    this.maskPipeline = NULL_HANDLE;
    this.blurPipeline = NULL_HANDLE;
    this.compositePipelineHDR = NULL_HANDLE;
    this.compositePipelineLDR = NULL_HANDLE;
    this.maskShader = NULL_HANDLE;
    this.blurShader = NULL_HANDLE;
    this.compositeShader = NULL_HANDLE;
  }
}
